import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Link,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { OnSiteChecklistSignature } from 'src/interfaces/OnSiteChecklistTypes';
import { UserType } from 'src/interfaces/UserTypes';

interface Props {
  signature: OnSiteChecklistSignature;
  disabled: boolean;
  currentUserType: UserType;
  canSign: boolean; // all required installation details filled
  onRemarksChange: (remarks: string) => Promise<boolean>;
  onSign: (signedByName: string) => Promise<boolean>;
  onResetSignature: () => Promise<boolean>;
}

function OnSiteSignatureBlock({
  signature,
  disabled,
  currentUserType,
  canSign,
  onRemarksChange,
  onSign,
  onResetSignature
}: Props) {
  const intl = useIntl();
  const [remarks, setRemarks] = useState(signature.remarks ?? '');
  const [signedByName, setSignedByName] = useState('');
  const [consentExpanded, setConsentExpanded] = useState(false);

  useEffect(() => {
    setRemarks(signature.remarks ?? '');
  }, [signature.remarks]);

  const isSigned = !!signature.signedAt;
  const canResetSignature = isSigned && currentUserType === UserType.admin;

  const handleRemarksBlur = async () => {
    if (disabled) return;
    if (remarks !== (signature.remarks ?? '')) {
      await onRemarksChange(remarks);
    }
  };

  const handleSign = async () => {
    if (!signedByName.trim()) return;
    await onSign(signedByName.trim());
  };

  const formatSignedDate = (iso: string | null): string => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            <FormattedMessage
              id="onSiteRemarksTitle"
              defaultMessage="Bemerkungen vom Installateur"
            />
          </Typography>
          <TextField
            multiline
            minRows={4}
            maxRows={12}
            fullWidth
            value={remarks}
            disabled={disabled}
            onChange={(e) => setRemarks(e.target.value)}
            onBlur={handleRemarksBlur}
            placeholder={intl.formatMessage({
              id: 'onSiteRemarksPlaceholder',
              defaultMessage: 'Zusätzliche Bemerkungen…'
            })}
          />
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            <FormattedMessage id="onSiteSignatureTitle" defaultMessage="E-Signatur" />
          </Typography>

          {isSigned ? (
            <Alert
              severity="success"
              action={
                canResetSignature ? (
                  <Button color="inherit" size="small" onClick={onResetSignature}>
                    <FormattedMessage
                      id="onSiteResetSignature"
                      defaultMessage="Unterschrift zurücksetzen"
                    />
                  </Button>
                ) : undefined
              }
            >
              <FormattedMessage
                id="onSiteSignedBy"
                defaultMessage="Unterzeichnet von {name} am {date}"
                values={{
                  name: signature.signedByName ?? '',
                  date: formatSignedDate(signature.signedAt)
                }}
              />
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  <FormattedMessage
                    id="onSiteConsentShort"
                    defaultMessage="Ich bestätige, dass die Installation gemäss obiger Checkliste fachgerecht ausgeführt und in Betrieb genommen wurde."
                  />
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setConsentExpanded((v) => !v)}
                  sx={{ mt: 0.5 }}
                >
                  {consentExpanded ? (
                    <FormattedMessage id="onSiteShowLess" defaultMessage="Weniger anzeigen" />
                  ) : (
                    <FormattedMessage id="onSiteShowMore" defaultMessage="Mehr anzeigen" />
                  )}
                </Link>
                <Collapse in={consentExpanded}>
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                    <FormattedMessage
                      id="onSiteConsentLong"
                      defaultMessage="Mit der elektronischen Unterschrift bestätigt der ausführende Installateur, dass die Installation gemäss obiger Checkliste und den anerkannten Regeln der Technik ausgeführt und in Betrieb genommen wurde. Die obenstehenden Angaben sind vollständig und wahrheitsgetreu. Diese Bestätigung ist Teil der Inbetriebnahme und wird zusammen mit der Checkliste an inesco energy übermittelt."
                    />
                  </Typography>
                </Collapse>
              </Box>

              <TextField
                label={intl.formatMessage({
                  id: 'onSiteFullName',
                  defaultMessage: 'Vollständiger Name'
                })}
                required
                value={signedByName}
                onChange={(e) => setSignedByName(e.target.value)}
                disabled={disabled}
                sx={{ maxWidth: 480 }}
              />

              {!canSign && (
                <Alert severity="warning">
                  <FormattedMessage
                    id="fillRequiredBeforeSign"
                    defaultMessage="Please fill all required (*) fields in the installation details before signing."
                  />
                </Alert>
              )}

              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled || !signedByName.trim() || !canSign}
                  onClick={handleSign}
                >
                  <FormattedMessage id="onSiteSignButton" defaultMessage="Unterschreiben" />
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default OnSiteSignatureBlock;
